import { getBlogPosts } from "../data/blogPosts";
import { toursData } from "../data/tours";

export const CANONICAL_ORIGIN = "https://istanbulescape.com";

const staticPaths = ["/", "/about", "/experiences", "/contact", "/blog", "/vip-transfers"] as const;

export type CrawlRoute = {
  path: string;
  indexable: boolean;
};

function decodePath(url: string) {
  const rawPath = url.split("?")[0] || "/";
  try {
    return decodeURI(rawPath).replace(/\/+$/, "") || "/";
  } catch {
    return rawPath.replace(/\/+$/, "") || "/";
  }
}

export function getCrawlRoute(url: string): CrawlRoute {
  const path = decodePath(url);

  if ((staticPaths as readonly string[]).includes(path)) {
    return { path, indexable: true };
  }

  const tourMatch = path.match(/^\/tours\/([^/]+)$/);
  if (tourMatch && toursData.some((tour) => tour.slug === tourMatch[1])) {
    return { path, indexable: true };
  }

  const blogMatch = path.match(/^\/blog\/([^/]+)$/);
  if (blogMatch && getBlogPosts("en").some((post) => post.slug === blogMatch[1])) {
    return { path, indexable: true };
  }

  return { path, indexable: false };
}

export function getIndexablePaths() {
  return [
    ...staticPaths,
    ...toursData.map((tour) => `/tours/${tour.slug}`),
    ...getBlogPosts("en").map((post) => `/blog/${post.slug}`),
  ];
}

export function getLocaleUrl(path: string, language: "en" | "tr") {
  return `${CANONICAL_ORIGIN}${path}?lang=${language}`;
}

function escapeXml(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

export function buildSitemapXml() {
  const entries = getIndexablePaths().flatMap((path) => {
    const en = getLocaleUrl(path, "en");
    const tr = getLocaleUrl(path, "tr");
    const alternates = [
      `    <xhtml:link rel="alternate" hreflang="en" href="${escapeXml(en)}" />`,
      `    <xhtml:link rel="alternate" hreflang="tr" href="${escapeXml(tr)}" />`,
      `    <xhtml:link rel="alternate" hreflang="x-default" href="${escapeXml(en)}" />`,
    ];
    return [en, tr].map((url) => [
      "  <url>",
      `    <loc>${escapeXml(url)}</loc>`,
      ...alternates,
      "  </url>",
    ].join("\n"));
  });

  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9" xmlns:xhtml="http://www.w3.org/1999/xhtml">',
    ...entries,
    "</urlset>",
  ].join("\n");
}
